import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import {
  AsyncPipe,
  NgClass,
  NgComponentOutlet,
  NgTemplateOutlet,
} from '@angular/common';
import { B2bBreadcrumbComponent } from '@app/new-ui-lib/b2b-breadcrumb/components/b2b-breadcrumb/b2b-breadcrumb.component';
import { B2bDynamicDataCardComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-dynamic-data-card/b2b-dynamic-data-card.component';
import { B2bDynamicLoaderPipe } from '@app/b2b-dynamic-lib/pipes/b2b-dynamic-loader-pipe.pipe';
import { B2bFilterButtonsComponent } from '@app/new-ui-lib/b2b-buttons/components/b2b-filter-buttons/b2b-filter-buttons.component';
import { B2bHeadingComponent } from '@app/new-ui-lib/b2b-typography/components/b2b-heading/b2b-heading.component';
import { ContainerModel, LayoutModel } from '@app/models/server/layout.model';
import { PAGE_TYPES } from '@app/constants/app-settings';
import { fromEvent, Subscription } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { SeoSetupService } from '@b2b/seo-util-lib';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DesktopWebListPageDataService } from '../../services/desktop-web-list-page-data.service';
import { B2bShortstayFormComponent } from '@app/new-ui-lib/b2b-search/components/b2b-desktop/b2b-shortstay-form/b2b-shortstay-form.component';
import { B2bDesktopFilterCardComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-desktop-filter-card/b2b-desktop-filter-card.component';
import { FeatureListInfiniteScrollComponent } from '@app/new-ui-lib/b2b-list/components/b2b-feature-list/feature-list-infinite-scroll/feature-list-infinite-scroll.component';
import { B2bOverlayCardComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-overlay-card/b2b-overlay-card.component';
import { SmallPropertyCardV2Component } from '@app/new-ui-lib/b2b-cards/components/small-property-card-v2/small-property-card-v2.component';
import { convertQueryStringToObject } from '@app/services/util-services/convert-query-string-to-object/convert-query-string-to-object';

@Component({
  selector: 'lib-desktop-web-property-list',
  templateUrl: './desktop-web-property-list.component.html',
  styleUrls: ['./desktop-web-property-list.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    B2bBreadcrumbComponent,
    B2bDynamicDataCardComponent,
    B2bDynamicLoaderPipe,
    B2bFilterButtonsComponent,
    B2bHeadingComponent,
    NgComponentOutlet,
    NgTemplateOutlet,
    B2bShortstayFormComponent,
    B2bDesktopFilterCardComponent,
    FeatureListInfiniteScrollComponent,
    B2bOverlayCardComponent,
    SmallPropertyCardV2Component,
    NgClass,
  ],
  providers: [DesktopWebListPageDataService],
})
export class DesktopWebPropertyListComponent implements OnInit, OnDestroy {
  @Input() layoutData: LayoutModel;
  errorContainers: ContainerModel[] = [];
  pageType: string = PAGE_TYPES.LIST_PAGE;

  searchQuerySubscription: Subscription;
  initFeatureListPageSubscribe$: Subscription;
  initSeoDataSubscription$: Subscription;
  backButtonSubscription$: Subscription;

  loadOnClient: boolean;
  skeletonKeys: {
    headerLoaded: boolean;
    errorLoaded: boolean;
  } = {
    headerLoaded: false,
    errorLoaded: false,
  };

  isServer = this.b2bDeviceUtilService.isServer();
  isSeoPage: boolean = false;
  pageTypes = PAGE_TYPES;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private desktopWebListPageDataService: DesktopWebListPageDataService,
    private eventService: EventService,
    private seoSetupService: SeoSetupService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    @Optional() @Inject('WINDOW') private window: any,
  ) {}

  ngOnInit() {
    this.loadOnClient = this.route?.snapshot?.data?.loadOnClient;

    if (this.router.url?.includes(`/feature/list`)) {
      this.isSeoPage = false;
      if (this.loadOnClient) {
        if (!this.b2bDeviceUtilService.isServer()) {
          this.resetLayoutData(
            '/feature/list',
            this.route?.snapshot?.queryParams,
            false,
          );
        }
      } else {
        this.resetLayoutData(
          '/feature/list',
          this.route?.snapshot?.queryParams,
          false,
        );
      }
    } else if (this.layoutData?.mainContainer?.length > 0) {
      // no need to do anything
    } else {
      this.loadSeoData();
      this.isSeoPage = true;
    }

    this.resetFilterValueContainers();
    this.searchQuerySubscription =
      this.eventService.searchQueryChangeEvent.subscribe({
        next: (result: { queryParams?: any; resetLayoutData: boolean }) => {
          if (result?.resetLayoutData) {
            this.resetLayoutData('/feature/list', result?.queryParams, false);
          } else {
            this.resetLayoutData('/feature/list', result?.queryParams, true);
          }
        },
      });

    if (!this.b2bDeviceUtilService?.isServer()) {
      this.backButtonSubscription$ = fromEvent(
        this.window,
        'popstate',
      ).subscribe((e: PopStateEvent) => {
        const location = (e?.currentTarget as Window)?.location;
        const url = location?.href;
        if (url?.indexOf('feature/list') > -1) {
          const search = location?.search;
          const newParams = convertQueryStringToObject(search);
          this.resetLayoutData('/feature/list', newParams, false);
        }
      });
    }
  }

  loadSeoData() {
    this.skeletonKeys.errorLoaded = false;
    this.initSeoDataSubscription$ = this.seoSetupService
      .initSeoData()
      .subscribe({
        next: (layoutData: LayoutModel) => {
          this.layoutData = layoutData;
          this.resetFilterValueContainers();
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (errorResponse?.error?.errorContainer?.length > 0) {
            this.errorContainers = errorResponse?.error?.errorContainer;
          }
          this.skeletonKeys.errorLoaded = true;
        },
      });
  }

  resetLayoutData(
    urlPath: string,
    queryParams: Params,
    resetOnlyMainContainer: boolean,
  ) {
    this.skeletonKeys.errorLoaded = false;
    this.initFeatureListPageSubscribe$ = this.desktopWebListPageDataService
      .initFeatureListPage({
        path: urlPath,
        queryParams,
      })
      .subscribe({
        next: (layoutData: LayoutModel) => {
          if (layoutData?.mainContainer?.length > 0) {
            if (resetOnlyMainContainer) {
              this.layoutData.mainContainer = layoutData.mainContainer;
            } else {
              this.layoutData = layoutData;
            }
            this.resetFilterValueContainers();
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (errorResponse?.error?.errorContainer?.length > 0) {
            this.errorContainers = errorResponse?.error?.errorContainer;
          }
          this.skeletonKeys.errorLoaded = true;
        },
      });
  }

  resetFilterValueContainers() {
    this.desktopWebListPageDataService.setFilterValueContainer(
      this.layoutData?.filterValueContainers,
    );
  }

  unsubscribe() {
    if (this.initFeatureListPageSubscribe$) {
      this.initFeatureListPageSubscribe$?.unsubscribe();
    }
    if (this.searchQuerySubscription) {
      this.searchQuerySubscription?.unsubscribe();
    }
    if (this.initSeoDataSubscription$) {
      this.initSeoDataSubscription$.unsubscribe();
    }
    if (this.backButtonSubscription$) {
      this.backButtonSubscription$.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
